import { PetSelfieFieldsFragment } from '~graphql/generated/graphql'
import { LocalStorageKeys } from '~config/constants'
import { getPatientAge, getPatientBreed } from '~utils/helpers'

export function getPetSelfieDisplayUrl(
    selfie: PetSelfieFieldsFragment,
): string {
    return selfie.display_media_url || selfie.submitted_media_url
}

export function getPetSelfieTitle(selfie: PetSelfieFieldsFragment): string {
    return (
        selfie.pet_name?.trim() || selfie.patient?.name?.trim() || 'Pet selfie'
    )
}

export function getPetSelfieBreedAndAge(
    selfie: PetSelfieFieldsFragment,
): string | null {
    const patient = selfie.patient
    if (!patient) return null

    const parts: string[] = []
    const breed = getPatientBreed(patient)?.trim()
    if (breed) parts.push(breed)

    if (patient.birthday) {
        parts.push(getPatientAge(patient.birthday))
    }

    return parts.length > 0 ? parts.join(' · ') : null
}

export function isPetSelfieAwaitingApproval(
    selfie: PetSelfieFieldsFragment,
): boolean {
    return !selfie.is_archived && !selfie.in_slideshow && !selfie.is_excluded
}

export type PetSelfieFilterTab =
    | 'awaiting_approval'
    | 'slideshow'
    | 'excluded'
    | 'archived'

export type PetSelfiePageSection = 'selfies' | 'settings'

export const PET_SELFIE_SLIDESHOW_INTERVAL_OPTIONS = [5, 10, 20, 30] as const

export type PetSelfieSlideshowIntervalSeconds =
    (typeof PET_SELFIE_SLIDESHOW_INTERVAL_OPTIONS)[number]

export const DEFAULT_PET_SELFIE_SLIDESHOW_INTERVAL_SECONDS: PetSelfieSlideshowIntervalSeconds = 10

export const PET_SELFIE_SLIDESHOW_COLOR_OPTIONS = [
    'black',
    'white',
    'primary',
    'accent',
] as const

export type PetSelfieSlideshowColorOption =
    (typeof PET_SELFIE_SLIDESHOW_COLOR_OPTIONS)[number]

export type PetSelfieSlideshowTheme = {
    background: PetSelfieSlideshowColorOption
    text: PetSelfieSlideshowColorOption
    showBreedAndAge: boolean
    hideAllCaptions: boolean
    shuffleOrder: boolean
    intervalSeconds: PetSelfieSlideshowIntervalSeconds
}

export const DEFAULT_PET_SELFIE_SLIDESHOW_THEME: PetSelfieSlideshowTheme = {
    background: 'black',
    text: 'white',
    showBreedAndAge: false,
    hideAllCaptions: false,
    shuffleOrder: false,
    intervalSeconds: DEFAULT_PET_SELFIE_SLIDESHOW_INTERVAL_SECONDS,
}

export type ClinicBrandColors = {
    primary?: string | null
    accent?: string | null
}

function isSlideshowColorOption(
    value: unknown,
): value is PetSelfieSlideshowColorOption {
    return (
        typeof value === 'string' &&
        (PET_SELFIE_SLIDESHOW_COLOR_OPTIONS as readonly string[]).includes(
            value,
        )
    )
}

function isSlideshowIntervalSeconds(
    value: unknown,
): value is PetSelfieSlideshowIntervalSeconds {
    return (
        typeof value === 'number' &&
        (PET_SELFIE_SLIDESHOW_INTERVAL_OPTIONS as readonly number[]).includes(
            value,
        )
    )
}

function themeStorageKey(clinicId: string): string {
    return `${LocalStorageKeys.petSelfieSlideshowTheme}:${clinicId}`
}

export function getPetSelfieSlideshowTheme(
    clinicId: string,
): PetSelfieSlideshowTheme {
    try {
        const raw = localStorage.getItem(themeStorageKey(clinicId))
        if (!raw) return DEFAULT_PET_SELFIE_SLIDESHOW_THEME

        const parsed: unknown = JSON.parse(raw)
        if (!parsed || typeof parsed !== 'object') {
            return DEFAULT_PET_SELFIE_SLIDESHOW_THEME
        }

        const background = (parsed as { background?: unknown }).background
        const text = (parsed as { text?: unknown }).text
        const showBreedAndAge = (parsed as { showBreedAndAge?: unknown })
            .showBreedAndAge
        const hideAllCaptions = (parsed as { hideAllCaptions?: unknown })
            .hideAllCaptions
        const shuffleOrder = (parsed as { shuffleOrder?: unknown }).shuffleOrder
        const intervalSeconds = (parsed as { intervalSeconds?: unknown })
            .intervalSeconds

        return {
            background: isSlideshowColorOption(background)
                ? background
                : DEFAULT_PET_SELFIE_SLIDESHOW_THEME.background,
            text: isSlideshowColorOption(text)
                ? text
                : DEFAULT_PET_SELFIE_SLIDESHOW_THEME.text,
            showBreedAndAge:
                typeof showBreedAndAge === 'boolean'
                    ? showBreedAndAge
                    : DEFAULT_PET_SELFIE_SLIDESHOW_THEME.showBreedAndAge,
            hideAllCaptions:
                typeof hideAllCaptions === 'boolean'
                    ? hideAllCaptions
                    : DEFAULT_PET_SELFIE_SLIDESHOW_THEME.hideAllCaptions,
            shuffleOrder:
                typeof shuffleOrder === 'boolean'
                    ? shuffleOrder
                    : DEFAULT_PET_SELFIE_SLIDESHOW_THEME.shuffleOrder,
            intervalSeconds: isSlideshowIntervalSeconds(intervalSeconds)
                ? intervalSeconds
                : DEFAULT_PET_SELFIE_SLIDESHOW_THEME.intervalSeconds,
        }
    } catch {
        return DEFAULT_PET_SELFIE_SLIDESHOW_THEME
    }
}

export function setPetSelfieSlideshowTheme(
    clinicId: string,
    theme: PetSelfieSlideshowTheme,
): void {
    localStorage.setItem(themeStorageKey(clinicId), JSON.stringify(theme))
}

export function resolvePetSelfieSlideshowColor(
    option: PetSelfieSlideshowColorOption,
    clinicColors: ClinicBrandColors,
): string {
    switch (option) {
        case 'black':
            return '#000000'
        case 'white':
            return '#FFFFFF'
        case 'primary':
            return clinicColors.primary?.trim() || '#1A365D'
        case 'accent':
            return (
                clinicColors.accent?.trim() ||
                clinicColors.primary?.trim() ||
                '#3182CE'
            )
        default: {
            const _exhaustive: never = option
            return _exhaustive
        }
    }
}

export function getPetSelfieSlideshowColorLabel(
    option: PetSelfieSlideshowColorOption,
): string {
    switch (option) {
        case 'black':
            return 'Black'
        case 'white':
            return 'White'
        case 'primary':
            return 'Clinic primary'
        case 'accent':
            return 'Clinic accent'
        default: {
            const _exhaustive: never = option
            return _exhaustive
        }
    }
}

function excludedCaptionIdsStorageKey(clinicId: string): string {
    return `${LocalStorageKeys.petSelfieSlideshowExcludedCaptionIds}:${clinicId}`
}

export function getPetSelfieSlideshowExcludedCaptionIds(
    clinicId: string,
): string[] {
    try {
        const raw = localStorage.getItem(excludedCaptionIdsStorageKey(clinicId))
        if (!raw) return []

        const parsed: unknown = JSON.parse(raw)
        if (!Array.isArray(parsed)) return []

        return parsed.filter((id): id is string => typeof id === 'string')
    } catch {
        return []
    }
}

export function setPetSelfieSlideshowExcludedCaptionIds(
    clinicId: string,
    selfieIds: string[],
): void {
    localStorage.setItem(
        excludedCaptionIdsStorageKey(clinicId),
        JSON.stringify(selfieIds),
    )
}

export function setPetSelfieCaptionExcludedFromSlideshow(
    clinicId: string,
    selfieId: string,
    excluded: boolean,
): string[] {
    const current = new Set(getPetSelfieSlideshowExcludedCaptionIds(clinicId))
    if (excluded) {
        current.add(selfieId)
    } else {
        current.delete(selfieId)
    }

    const next = Array.from(current)
    setPetSelfieSlideshowExcludedCaptionIds(clinicId, next)
    return next
}

/** Fisher–Yates shuffle; returns a new array. */
export function shufflePetSelfies<T>(items: T[]): T[] {
    const next = [...items]
    for (let i = next.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1))
        const temp = next[i]
        next[i] = next[j]
        next[j] = temp
    }
    return next
}
